import React from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Skeleton } from "@/components/ui/skeleton"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PhoneInputField } from "@/components/ui/phone-input"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

// Import hooks
import {
  useBuilding,
  useBuildingStats,
  useUpdateBuilding,
  useDeleteBuilding,
  useImportBuildingUnitsCSV,
  useImportBuildingResidentsCSV,
} from "@/hooks/useBuildings"
import { useCreateUnit, useUnitsByBuilding, useUpdateUnit, useDeleteUnit, useUnit, useDownloadUnitCSVTemplate } from "@/hooks/useUnits"
import { useCreateResident, useResidents, useUpdateResident, useDeleteResident, useResident, useDownloadResidentCSVTemplate } from "@/hooks/useResidents"
import {
  getBuildingPaymentConfig,
  updateBuildingPaymentConfig,
  type Building,
  type BuildingStats,
  type BuildingPaymentConfigResponse,
  type UpdateBuildingPaymentConfigData,
} from "@/api/buildings"
import type { CreateUnitData, UnitStatus, UpdateUnitData, UnitList } from "@/api/units"
import type { CreateResidentData, ResidentRole, UpdateResidentData, ResidentList } from "@/api/residents"
import BulkImportModal from "@/components/BulkImportModal"
import { toast } from "sonner"
import { useLanguage } from "@/contexts/LanguageContext"

// Icons
import {
  ArrowLeft,
  Building2,
  MapPin,
  Edit,
  Trash2,
  Users,
  Home,
  TrendingUp,
  DollarSign,
  CheckCircle2,
  XCircle,
  Upload,
  Layers,
  Plus,
  Eye,
  MoreHorizontal,
  Search,
} from "lucide-react"

/** =========================================================
 * Design Tokens
 * ========================================================= */
const PRIMARY = "#1F3A93"
const QUIET =
  "!bg-white !text-gray-900 !border-black/10 border ring-1 ring-black/5 shadow-sm rounded-xl hover:!bg-gray-50 active:!bg-gray-50"
const MAX_ID_DOC_UPLOAD_MB = 10
const MAX_ID_DOC_UPLOAD_BYTES = MAX_ID_DOC_UPLOAD_MB * 1024 * 1024

type BuildingPaymentFormData = {
  bank_name: string
  bank_account_number: string
  bank_routing_number: string
  account_holder_name: string
  zelle_email: string
  zelle_phone: string
  paypal_email: string
  pago_movil_bank: string
  pago_movil_phone: string
  pago_movil_id_number: string
  cash_instructions: string
}

function getEmptyBuildingPaymentFormData(): BuildingPaymentFormData {
  return {
    bank_name: "",
    bank_account_number: "",
    bank_routing_number: "",
    account_holder_name: "",
    zelle_email: "",
    zelle_phone: "",
    paypal_email: "",
    pago_movil_bank: "",
    pago_movil_phone: "",
    pago_movil_id_number: "",
    cash_instructions: "",
  }
}

function hasAnyPaymentFormValue(data: BuildingPaymentFormData): boolean {
  return Object.values(data).some((value) => String(value || "").trim().length > 0)
}

type TranslateFn = (key: string, params?: Record<string, string | number>) => string

function getResidentIdDocValidationMessage(file: File, t: TranslateFn): string | null {
  const validType = file.type.startsWith("image/")
  if (!validType) {
    return t("validation.invalidFileType", { types: "image/*" })
  }
  if (file.size > MAX_ID_DOC_UPLOAD_BYTES) {
    return t("validation.fileTooLarge", { size: MAX_ID_DOC_UPLOAD_MB })
  }
  return null
}

function getLocale(language: string): string {
  return language === "es" ? "es-ES" : "en-US"
}

function formatCurrencyValue(
  value: number | string,
  language: string,
  minimumFractionDigits = 2,
  maximumFractionDigits = 2
): string {
  const numberValue = typeof value === "number" ? value : Number.parseFloat(String(value) || "0")
  return new Intl.NumberFormat(getLocale(language), {
    style: "currency",
    currency: "USD",
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Number.isFinite(numberValue) ? numberValue : 0)
}

function formatDateValue(value: string, language: string): string {
  return new Intl.DateTimeFormat(getLocale(language)).format(new Date(value))
}

function getUnitStatusLabel(status: string, t: TranslateFn): string {
  const normalized = String(status || "").toLowerCase()
  if (normalized === "occupied") return t("hoa.buildingDetail.units.status.occupied")
  if (normalized === "vacant") return t("hoa.buildingDetail.units.status.vacant")
  if (normalized === "inactive") return t("hoa.buildingDetail.units.status.inactive")
  return status
}

function getResidentRoleLabel(role: string, t: TranslateFn): string {
  return String(role || "").toLowerCase() === "tenant"
    ? t("hoa.buildingDetail.residents.role.tenant")
    : t("hoa.buildingDetail.residents.role.owner")
}

function getContactTypeLabel(isPrimary: boolean, t: TranslateFn): string {
  return isPrimary
    ? t("hoa.buildingDetail.residents.contact.primary")
    : t("hoa.buildingDetail.residents.contact.secondary")
}

/** =========================================================
 * Main Component
 * ========================================================= */
export default function BuildingDetailPage() {
  const { t, language } = useLanguage()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = React.useState("overview")
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false)
  const [showUnitsImport, setShowUnitsImport] = React.useState(false)
  const [showResidentsImport, setShowResidentsImport] = React.useState(false)
  const [showAddUnit, setShowAddUnit] = React.useState(false)
  const [showAddResident, setShowAddResident] = React.useState(false)

  // Search/filter states
  const [unitSearch, setUnitSearch] = React.useState("")
  const [unitStatusFilter, setUnitStatusFilter] = React.useState<"all" | "occupied" | "vacant" | "inactive">("all")
  const [residentSearch, setResidentSearch] = React.useState("")

  // Unit action states
  const [selectedUnit, setSelectedUnit] = React.useState<UnitList | null>(null)
  const [showViewUnit, setShowViewUnit] = React.useState(false)
  const [showEditUnit, setShowEditUnit] = React.useState(false)
  const [showDeleteUnit, setShowDeleteUnit] = React.useState(false)

  // Resident action states
  const [selectedResident, setSelectedResident] = React.useState<ResidentList | null>(null)
  const [showViewResident, setShowViewResident] = React.useState(false)
  const [showEditResident, setShowEditResident] = React.useState(false)
  const [showDeleteResident, setShowDeleteResident] = React.useState(false)

  // Building edit modal state
  const [showEditBuilding, setShowEditBuilding] = React.useState(false)
  const [paymentFormData, setPaymentFormData] = React.useState<BuildingPaymentFormData>(
    getEmptyBuildingPaymentFormData()
  )


  // Fetch building data and stats
  const buildingQ = useBuilding(id!)
  const buildingPaymentConfigQ = useQuery({
    queryKey: ["building-payment-config", id],
    queryFn: () => getBuildingPaymentConfig(id!),
    enabled: !!id,
  })
  const statsQ = useBuildingStats(id!)
  const unitsQ = useUnitsByBuilding(id!)  // Fetch only units for this building
  const residentsQ = useResidents()
  const updateBuildingMut = useUpdateBuilding()
  const updateBuildingPaymentMut = useMutation({
    mutationFn: ({ buildingId, data }: { buildingId: string; data: UpdateBuildingPaymentConfigData }) =>
      updateBuildingPaymentConfig(buildingId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["building-payment-config", id] })
      queryClient.invalidateQueries({ queryKey: ["buildings"] })
    },
  })
  const deleteMut = useDeleteBuilding()
  const importUnitsMut = useImportBuildingUnitsCSV()
  const importResidentsMut = useImportBuildingResidentsCSV()
  const downloadUnitTemplateMut = useDownloadUnitCSVTemplate()
  const downloadResidentTemplateMut = useDownloadResidentCSVTemplate()

  const building = buildingQ.data
  const stats = statsQ.data
  const paymentStatus = buildingPaymentConfigQ.data?.payment_config_status ?? "MISSING"

  React.useEffect(() => {
    if (!showEditBuilding) return
    const config = buildingPaymentConfigQ.data?.config
    if (!config) {
      setPaymentFormData(getEmptyBuildingPaymentFormData())
      return
    }
    setPaymentFormData({
      bank_name: config.bank_name ?? "",
      bank_account_number: config.bank_account_number ?? "",
      bank_routing_number: config.bank_routing_number ?? "",
      account_holder_name: config.account_holder_name ?? "",
      zelle_email: config.zelle_email ?? "",
      zelle_phone: config.zelle_phone ?? "",
      paypal_email: config.paypal_email ?? "",
      pago_movil_bank: config.pago_movil_bank ?? "",
      pago_movil_phone: config.pago_movil_phone ?? "",
      pago_movil_id_number: config.pago_movil_id_number ?? "",
      cash_instructions: config.cash_instructions ?? "",
    })
  }, [showEditBuilding, buildingPaymentConfigQ.data?.config])

  // Units are now fetched directly for this building (no client-side filter needed)
  const rawBuildingUnits = unitsQ.data ?? []

  const hasUnits = rawBuildingUnits.length > 0
  const vacantUnits = React.useMemo(
    () => rawBuildingUnits.filter((u) => {
      // Consider a unit as "vacant/available" if:
      // 1. It has vacant status, OR
      // 2. It has no residents (regardless of status, except inactive)
      return (u.status === "vacant" || u.resident_count === 0) && u.status !== "inactive"
    }),
    [rawBuildingUnits]
  )
  const hasVacantUnits = vacantUnits.length > 0

  // Filter units by search and status
  const buildingUnits = React.useMemo(() => {
    return rawBuildingUnits.filter(unit => {
      // Status filter
      if (unitStatusFilter !== "all" && unit.status !== unitStatusFilter) return false
      // Search filter
      if (unitSearch) {
        const hay = [unit.unit_code, unit.floor, unit.number].join(" ").toLowerCase()
        return hay.includes(unitSearch.toLowerCase())
      }
      return true
    })
  }, [rawBuildingUnits, unitSearch, unitStatusFilter])

  // Get all residents for this building (unfiltered, for selection)
  const allBuildingResidents = React.useMemo(() => {
    return (residentsQ.data ?? []).filter(resident => {
      const residentUnit = rawBuildingUnits.find(u => u.unit_code === resident.unit_code)
      return !!residentUnit
    })
  }, [residentsQ.data, rawBuildingUnits])

  // Filtered residents for display
  const buildingResidents = React.useMemo(() => {
    return allBuildingResidents.filter(resident => {
      if (residentSearch) {
        const hay = [
          resident.full_name, 
          resident.email, 
          resident.phone || "", 
          resident.unit_code
        ].join(" ").toLowerCase()
        return hay.includes(residentSearch.toLowerCase())
      }
      return true
    })
  }, [allBuildingResidents, residentSearch])

  const addResidentDisabled = unitsQ.isLoading || !hasUnits || !hasVacantUnits
  const addResidentTooltip = unitsQ.isLoading
    ? t("hoa.buildingDetail.loadingUnits")
    : !hasUnits
      ? t("hoa.buildingDetail.residents.emptyUnits")
      : !hasVacantUnits
          ? t("hoa.buildingDetail.noVacantUnits")
          : undefined
  const handleOpenAddUnitFromResidentFlow = () => {
    setShowAddResident(false)
    setShowAddUnit(true)
  }

  const handleDelete = () => {
    deleteMut.mutate(id!, {
      onSuccess: () => {
        navigate("/hoa-admin/members")
      },
    })
  }

  if (buildingQ.isLoading) {
    return <BuildingDetailSkeleton />
  }

  if (buildingQ.isError || !building) {
    return (
      <div className="space-y-5 relative">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />
        <Card variant="glass" className="rounded-3xl">
          <CardContent className="p-12 text-center">
            <Building2 className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t("hoa.buildingDetail.notFound.title")}</h2>
            <p className="text-sm text-gray-600 mb-6">
              {t("hoa.buildingDetail.notFound.description")}
            </p>
            <Button onClick={() => navigate("/hoa-admin/members")} className={QUIET}>
              <ArrowLeft className="h-4 w-4 mr-2 text-black" />
              {t("hoa.buildingDetail.backToBuildings")}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-5 sm:space-y-6 relative">
      {/* Soft ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
        <div className="flex-1 w-full space-y-3">
          <Button
            variant="outline"
            onClick={() => navigate("/hoa-admin/members")}
            className={QUIET}
          >
            <ArrowLeft className="h-4 w-4 mr-2  text-black" />
            {t("hoa.buildingDetail.backToBuildings")}
          </Button>

          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-[18px] sm:text-[20px] font-bold text-gray-900">{building.name}</h1>
              <Badge variant="secondary" className="text-xs font-medium">
                {building.code}
              </Badge>
              {building.is_active ? (
                <Badge className="text-xs bg-green-100 text-green-700 hover:bg-green-100">{t("hoa.buildingDetail.status.active")}</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">{t("hoa.buildingDetail.status.inactive")}</Badge>
              )}
            </div>

            {(building.city || building.state) && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">
                  {[building.address, building.city, building.state]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setShowEditBuilding(true)}
            className={`${QUIET} flex-1 sm:flex-none`}
          >
            <Edit className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("hoa.buildingDetail.edit")}</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowDeleteDialog(true)}
            className={`${QUIET} flex-1 sm:flex-none`}
          >
            <Trash2 className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("hoa.buildingDetail.delete")}</span>
          </Button>
        </div>
            {/* Edit Building Modal */}
            <Dialog open={showEditBuilding} onOpenChange={(isOpen) => { if (!isOpen) setShowEditBuilding(false) }}>
              <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
                <DialogHeader>
                  <DialogTitle className="text-xl">{t("hoa.buildingDetail.edit")}</DialogTitle>
                  <DialogDescription>
                    {t("hoa.buildingDetail.editBuildingDescription")}
                  </DialogDescription>
                </DialogHeader>
                {building && (
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.target as HTMLFormElement;
                      const formData = new FormData(form);
                      const data: any = {};
                      formData.forEach((value, key) => {
                         // Only add if not empty, OR specifically handle default_monthly_fee
                         if (value === "") {
                             if (key === "default_monthly_fee") return; // skip empty decimal
                         }
                         data[key] = value;
                      });

                      const paymentData: UpdateBuildingPaymentConfigData = {
                        bank_name: paymentFormData.bank_name.trim(),
                        bank_account_number: paymentFormData.bank_account_number.trim(),
                        bank_routing_number: paymentFormData.bank_routing_number.trim(),
                        account_holder_name: paymentFormData.account_holder_name.trim(),
                        zelle_email: paymentFormData.zelle_email.trim(),
                        zelle_phone: paymentFormData.zelle_phone.trim(),
                        paypal_email: paymentFormData.paypal_email.trim(),
                        pago_movil_bank: paymentFormData.pago_movil_bank.trim(),
                        pago_movil_phone: paymentFormData.pago_movil_phone.trim(),
                        pago_movil_id_number: paymentFormData.pago_movil_id_number.trim(),
                        cash_instructions: paymentFormData.cash_instructions.trim(),
                      }

                      const shouldSavePaymentConfig =
                        hasAnyPaymentFormValue(paymentFormData) ||
                        Boolean(buildingPaymentConfigQ.data?.config)

                      try {
                        await updateBuildingMut.mutateAsync({ id: building.id, data });
                        if (shouldSavePaymentConfig) {
                          await updateBuildingPaymentMut.mutateAsync({
                            buildingId: building.id,
                            data: paymentData,
                          })
                        }
                        toast.success(t("hoa.buildingDetail.toast.buildingUpdated"));
                        setShowEditBuilding(false);
                      } catch (error: any) {
                        console.error("Update failed", error);
                        // Extract DRF field errors if any
                        const data = error.response?.data || {};
                        let msg = error.message;
                        if (typeof data === 'object' && Object.keys(data).length > 0) {
                           msg = Object.values(data).flat().join(" ");
                        }
                        toast.error(msg || t("hoa.buildingDetail.toast.buildingUpdateFailed"));
                      }
                    }}
                    className="flex flex-col overflow-hidden"
                  >
                    <div className="overflow-y-auto px-1 -mx-1 space-y-6 py-4" style={{maxHeight: "calc(85vh - 180px)"}}>
                      <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.info.title")}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="code">{t("hoa.buildingDetail.info.buildingCode")} <span className="text-red-500">*</span></Label>
                            <Input id="code" name="code" defaultValue={building.code} required />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="name">{t("hoa.buildingDetail.info.buildingName")} <span className="text-red-500">*</span></Label>
                            <Input id="name" name="name" defaultValue={building.name} required />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="rif">{t("hoa.buildingDetail.rifLabel")}</Label>
                            <Input id="rif" name="rif" defaultValue={building.rif} placeholder={t("hoa.buildingDetail.rifPlaceholder")} />
                            <p className="text-xs text-gray-400">{t("hoa.buildingDetail.rifHelper")}</p>
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="default_monthly_fee">{t("hoa.buildingDetail.info.defaultMonthlyFee")} (USD)</Label>
                            <Input
                              id="default_monthly_fee"
                              name="default_monthly_fee"
                              type="number"
                              step="0.01"
                              min="0"
                              defaultValue={building.default_monthly_fee}
                              placeholder={t("hoa.buildingDetail.defaultMonthlyFeePlaceholder")}
                            />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="address">{t("hoa.buildingDetail.info.address")}</Label>
                            <Input id="address" name="address" defaultValue={building.address} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="city">{t("hoa.buildingDetail.info.city")}</Label>
                            <Input id="city" name="city" defaultValue={building.city} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">{t("hoa.buildingDetail.info.state")}</Label>
                            <Input id="state" name="state" defaultValue={building.state} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="postal_code">{t("hoa.buildingDetail.info.postalCode")}</Label>
                            <Input id="postal_code" name="postal_code" defaultValue={building.postal_code} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="country">{t("hoa.buildingDetail.info.country")}</Label>
                            <Input id="country" name="country" defaultValue={building.country} />
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-sm font-semibold text-gray-900">Payment Routing Details</h3>
                          <Badge
                            className={`text-[11px] ${
                              paymentStatus === "CONFIGURED"
                                ? "bg-emerald-100 text-emerald-700"
                                : paymentStatus === "HOA_DEFAULT"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {paymentStatus === "CONFIGURED"
                              ? "Building configured"
                              : paymentStatus === "HOA_DEFAULT"
                                ? "Using HOA default"
                                : "Missing destination"}
                          </Badge>
                        </div>

                        <p className="text-xs text-gray-500">
                          Optional. These details control where funds are forwarded for this building.
                        </p>

                        {buildingPaymentConfigQ.isLoading ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <Skeleton className="h-10 rounded-xl" />
                            <Skeleton className="h-10 rounded-xl" />
                            <Skeleton className="h-10 rounded-xl" />
                            <Skeleton className="h-10 rounded-xl" />
                          </div>
                        ) : (
                          <>
                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.bankTransferDetails")}</h4>
                              <div className="space-y-2">
                                <Label htmlFor="edit_payment_bank_name">{t("settings.bankName")}</Label>
                                <Input
                                  id="edit_payment_bank_name"
                                  value={paymentFormData.bank_name}
                                  onChange={(e) => setPaymentFormData((prev) => ({ ...prev, bank_name: e.target.value }))}
                                  placeholder={t("settings.exBanesco")}
                                />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_bank_account_number">{t("settings.accountNumber")}</Label>
                                  <Input
                                    id="edit_payment_bank_account_number"
                                    value={paymentFormData.bank_account_number}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, bank_account_number: e.target.value }))}
                                    placeholder={t("settings.accountNumberPlaceholder")}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_bank_routing_number">{t("settings.routingNumber")}</Label>
                                  <Input
                                    id="edit_payment_bank_routing_number"
                                    value={paymentFormData.bank_routing_number}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, bank_routing_number: e.target.value }))}
                                    placeholder={t("settings.routingPlaceholder")}
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="edit_payment_account_holder_name">{t("settings.accountHolderName")}</Label>
                                <Input
                                  id="edit_payment_account_holder_name"
                                  value={paymentFormData.account_holder_name}
                                  onChange={(e) => setPaymentFormData((prev) => ({ ...prev, account_holder_name: e.target.value }))}
                                  placeholder={t("settings.accountHolderPlaceholder")}
                                />
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.paymentMethod.zelle")}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_zelle_email">{t("settings.zelleEmail")}</Label>
                                  <Input
                                    id="edit_payment_zelle_email"
                                    type="email"
                                    value={paymentFormData.zelle_email}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, zelle_email: e.target.value }))}
                                    placeholder={t("settings.zelleEmailPlaceholder")}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_zelle_phone">{t("settings.zellePhone")}</Label>
                                  <Input
                                    id="edit_payment_zelle_phone"
                                    value={paymentFormData.zelle_phone}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, zelle_phone: e.target.value }))}
                                    placeholder="+1 (555) 123-4567"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.paymentMethod.pagoMovil")}</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_pago_movil_bank">{t("settings.bank")}</Label>
                                  <Input
                                    id="edit_payment_pago_movil_bank"
                                    value={paymentFormData.pago_movil_bank}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, pago_movil_bank: e.target.value }))}
                                    placeholder={t("settings.exMercantil")}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="edit_payment_pago_movil_id_number">{t("settings.idNumber")}</Label>
                                  <Input
                                    id="edit_payment_pago_movil_id_number"
                                    value={paymentFormData.pago_movil_id_number}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, pago_movil_id_number: e.target.value }))}
                                    placeholder={t("settings.exIdNumber")}
                                  />
                                </div>
                                <div className="space-y-2 sm:col-span-2">
                                  <Label htmlFor="edit_payment_pago_movil_phone">{t("settings.phone")}</Label>
                                  <Input
                                    id="edit_payment_pago_movil_phone"
                                    value={paymentFormData.pago_movil_phone}
                                    onChange={(e) => setPaymentFormData((prev) => ({ ...prev, pago_movil_phone: e.target.value }))}
                                    placeholder="+58 (414) 123-4567"
                                  />
                                </div>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t("settings.otherPaymentMethods")}</h4>
                              <div className="space-y-2">
                                <Label htmlFor="edit_payment_paypal_email">{t("settings.paypalEmail")}</Label>
                                <Input
                                  id="edit_payment_paypal_email"
                                  type="email"
                                  value={paymentFormData.paypal_email}
                                  onChange={(e) => setPaymentFormData((prev) => ({ ...prev, paypal_email: e.target.value }))}
                                  placeholder={t("settings.paypalEmailPlaceholder")}
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="edit_payment_cash_instructions">{t("settings.cashInstructionsTitle")}</Label>
                              <Input
                                id="edit_payment_cash_instructions"
                                value={paymentFormData.cash_instructions}
                                onChange={(e) => setPaymentFormData((prev) => ({ ...prev, cash_instructions: e.target.value }))}
                                placeholder={t("settings.cashInstructionsPlaceholder")}
                              />
                            </div>

                            {buildingPaymentConfigQ.data?.fallback_payment_details && paymentStatus !== "CONFIGURED" && (
                              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                                <p className="text-xs">
                                  This building currently falls back to HOA-level payment details until its own destination is configured.
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        type="submit"
                        className="bg-primary text-white"
                        disabled={updateBuildingMut.isPending || updateBuildingPaymentMut.isPending}
                      >
                        {t("common.saveChanges")}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowEditBuilding(false)}
                        disabled={updateBuildingMut.isPending || updateBuildingPaymentMut.isPending}
                      >
                        {t("common.cancel")}
                      </Button>
                    </DialogFooter>
                  </form>
                )}
              </DialogContent>
            </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3 h-11 rounded-2xl bg-white/90 backdrop-blur ring-1 ring-black/10 p-1">
          <TabsTrigger
            value="overview"
            className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#1F3A93] data-[state=active]:to-[#1F3A93]/90 data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {t("hoa.buildingDetail.tabs.overview")}
          </TabsTrigger>
          <TabsTrigger
            value="units"
            className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#1F3A93] data-[state=active]:to-[#1F3A93]/90 data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {t("hoa.buildingDetail.tabs.units")} ({stats?.total_units ?? building.unit_count})
          </TabsTrigger>
          <TabsTrigger
            value="residents"
            className="rounded-xl data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#1F3A93] data-[state=active]:to-[#1F3A93]/90 data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {t("hoa.buildingDetail.tabs.residents")} ({stats?.total_residents ?? building.resident_count})
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          <OverviewTab
            building={building}
            stats={stats}
            isLoadingStats={statsQ.isLoading}
            buildingPaymentConfig={buildingPaymentConfigQ.data}
            t={t}
            language={language}
          />
        </TabsContent>

        {/* Units Tab */}
        <TabsContent value="units" className="mt-6 space-y-4">
          {/* Toolbar: Search, Filter, Actions */}
          <div className="flex flex-col gap-3">
            {/* Search and Filter row */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Input
                  value={unitSearch}
                  onChange={(e) => setUnitSearch(e.target.value)}
                  placeholder={t("hoa.buildingDetail.units.search")}
                  className="h-10 rounded-xl pl-10 bg-white/90 backdrop-blur ring-1 ring-black/10 placeholder:text-gray-400"
                />
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              </div>
              <Select value={unitStatusFilter} onValueChange={(v) => setUnitStatusFilter(v as any)}>
                <SelectTrigger className={`w-full sm:w-[150px] h-10 rounded-xl ${QUIET}`}>
                  <SelectValue placeholder={t("common.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("hoa.buildingDetail.units.filter.allStatus")}</SelectItem>
                  <SelectItem value="occupied">{t("hoa.buildingDetail.units.status.occupied")}</SelectItem>
                  <SelectItem value="vacant">{t("hoa.buildingDetail.units.status.vacant")}</SelectItem>
                  <SelectItem value="inactive">{t("hoa.buildingDetail.units.status.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Actions row */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowUnitsImport(true)}
                className={`${QUIET} w-full sm:w-auto`}
              >
                <Upload className="h-4 w-4 mr-2" />
                {t("hoa.buildingDetail.units.importCSV")}
              </Button>
              <Button
                onClick={() => setShowAddUnit(true)}
                style={{ backgroundColor: PRIMARY }}
                className="text-white hover:opacity-90 rounded-xl h-10 px-4 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("hoa.buildingDetail.units.addUnit")}
              </Button>
            </div>
          </div>

          {unitsQ.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 rounded-3xl" />
              <Skeleton className="h-24 rounded-3xl" />
              <Skeleton className="h-24 rounded-3xl" />
            </div>
          ) : unitsQ.isError ? (
            <Card variant="glass" className="rounded-3xl">
              <CardContent className="p-8 text-center">
                <XCircle className="h-12 w-12 mx-auto text-red-400 mb-3" />
                <p className="text-sm text-red-600 font-semibold mb-2">{t("hoa.buildingDetail.units.errorLoading")}</p>
                <p className="text-xs text-gray-600 mb-4">{unitsQ.error?.message}</p>
                <Button 
                  variant="outline" 
                  onClick={() => unitsQ.refetch()}
                  className="mt-2"
                >
                  {t("hoa.buildingDetail.retry")}
                </Button>
              </CardContent>
            </Card>
          ) : rawBuildingUnits.length === 0 ? (
            <Card variant="glass" className="rounded-3xl">
              <CardContent className="p-8 text-center">
                <Home className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 font-semibold mb-2">{t("hoa.buildingDetail.units.emptyInBuilding")}</p>
                <p className="text-xs text-gray-500">
                  {t("hoa.buildingDetail.units.emptyHint")}
                </p>
              </CardContent>
            </Card>
          ) : buildingUnits.length === 0 ? (
            <Card variant="glass" className="rounded-3xl">
              <CardContent className="p-8 text-center">
                <Search className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 font-semibold mb-2">{t("hoa.buildingDetail.units.noMatching")}</p>
                <p className="text-xs text-gray-500">
                  {t("hoa.buildingDetail.adjustSearchOrFilter")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {buildingUnits.map((unit) => (
                <Card key={unit.id} variant="glass" className="rounded-3xl ring-1 ring-black/5 shadow-lg shadow-black/10">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="grid size-10 place-items-center rounded-2xl bg-white ring-1 ring-black/10 shadow-sm text-[#1F3A93] flex-shrink-0">
                          <Home className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[15px] font-semibold text-gray-900 truncate">
                            {t("hoa.buildingDetail.unitLabel", { code: unit.unit_code })}
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            {unit.floor && t("hoa.buildingDetail.floorLabel", { floor: unit.floor })}
                            {unit.floor && unit.number && " • "}
                            {unit.number && `#${unit.number}`}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge className={
                              unit.status === "occupied"
                                ? "bg-green-100 text-green-700 ring-1 ring-black/10 border-0"
                                : unit.status === "vacant"
                                  ? "bg-blue-100 text-blue-700 ring-1 ring-black/10 border-0"
                                  : "bg-gray-100 text-gray-700 ring-1 ring-black/10 border-0"
                            }>
                              {getUnitStatusLabel(unit.status, t)}
                            </Badge>
                            <span className="text-xs text-gray-600">
                              {`${formatCurrencyValue(unit.monthly_fee, language)}${t("hoa.buildingDetail.perMonthShort")}`}
                            </span>
                            {unit.resident_count > 0 && (
                              <span className="text-xs text-gray-600 flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {t("hoa.buildingDetail.units.residentCount", { count: unit.resident_count })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Actions dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            className={`size-9 rounded-xl ${QUIET} flex-shrink-0`}
                            aria-label={t("hoa.buildingDetail.units.actionsAria")}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 rounded-xl">
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedUnit(unit)
                              setShowViewUnit(true)
                            }}
                            className="cursor-pointer"
                          >
                            <Eye className="h-4 w-4 mr-2" />{t("common.viewDetails")}</DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedUnit(unit)
                              setShowEditUnit(true)
                            }}
                            className="cursor-pointer"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            {t("hoa.buildingDetail.units.modal.edit")}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => {
                              setSelectedUnit(unit)
                              setShowDeleteUnit(true)
                            }}
                            className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t("hoa.buildingDetail.units.modal.delete")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Residents Tab */}
        <TabsContent value="residents" className="mt-6 space-y-4">
          {/* Guidance / dependency alerts */}
          {!unitsQ.isLoading && !hasUnits && (
            <Card variant="glass" className="rounded-2xl border border-amber-200 bg-amber-50/80">
              <CardContent className="p-4 space-y-2 text-amber-800">
                <div className="font-semibold text-amber-900">{t("hoa.buildingDetail.residents.unitsRequired")}</div>
                <div className="text-sm">{t("hoa.buildingDetail.residents.assignmentWarning")}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => { setShowAddUnit(true); setActiveTab("units") }}>
                    {t("hoa.buildingDetail.createUnit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={QUIET}
                    onClick={() => setActiveTab("units")}
                  >
                    {t("hoa.buildingDetail.viewAllUnits")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!unitsQ.isLoading && hasUnits && !hasVacantUnits && (
            <Card variant="glass" className="rounded-2xl border border-amber-200 bg-amber-50/80">
              <CardContent className="p-4 flex flex-col gap-2 text-amber-800">
                <div className="font-semibold text-amber-900">{t("hoa.buildingDetail.noVacantUnits")}</div>
                <div className="text-sm">{t("hoa.buildingDetail.markUnitVacant")}</div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => { setShowAddUnit(true); setActiveTab("units") }}>
                    {t("hoa.buildingDetail.createUnit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={QUIET}
                    onClick={() => setActiveTab("units")}
                  >
                    {t("hoa.buildingDetail.viewAllUnits")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {/* Toolbar: Search, Selection, Actions */}
          <div className="flex flex-col gap-3">
            {/* Search row */}
            <div className="relative flex-1">
              <Input
                value={residentSearch}
                onChange={(e) => setResidentSearch(e.target.value)}
                placeholder={t("hoa.buildingDetail.residents.searchByFields")}
                className="h-10 rounded-xl pl-10 bg-white/90 backdrop-blur ring-1 ring-black/10 placeholder:text-gray-400"
              />
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
            </div>
            {/* Actions row */}
              <div className="flex flex-col sm:flex-row justify-between gap-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowResidentsImport(true)}
                  className={`${QUIET} w-full sm:w-auto`}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {t("hoa.buildingDetail.residents.importCSV")}
                </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={() => !addResidentDisabled && setShowAddResident(true)}
                          style={{ backgroundColor: addResidentDisabled ? undefined : PRIMARY }}
                          className={`hover:opacity-90 rounded-xl h-10 px-4 w-full sm:w-auto ${addResidentDisabled ? QUIET : "text-white"}`}
                          disabled={addResidentDisabled}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          {t("hoa.buildingDetail.residents.addResident")}
                        </Button>
                      </TooltipTrigger>
                      {addResidentTooltip && <TooltipContent>{addResidentTooltip}</TooltipContent>}
                    </Tooltip>
                  </TooltipProvider>
              </div>
            </div>
              {hasVacantUnits && (
                <div className="text-xs text-gray-600">{t("hoa.buildingDetail.residents.assignedToUnit")}</div>
              )}
          </div>

          {residentsQ.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-32 rounded-3xl" />
              <Skeleton className="h-32 rounded-3xl" />
            </div>
          ) : allBuildingResidents.length === 0 ? (
            <Card variant="glass" className="rounded-3xl">
              <CardContent className="p-8 text-center space-y-3">
                <Users className="h-12 w-12 mx-auto text-gray-400" />
                <div className="text-sm font-semibold text-gray-800">{t("hoa.buildingDetail.residents.unitsRequired")}</div>
                <div className="text-xs text-gray-600">{t("hoa.buildingDetail.residents.assignmentWarning")}</div>
                <div className="flex justify-center gap-2">
                  <Button size="sm" onClick={() => { setShowAddUnit(true); setActiveTab("units") }}>
                    {t("hoa.buildingDetail.createUnit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className={QUIET}
                    onClick={() => setActiveTab("units")}
                  >
                    {t("hoa.buildingDetail.viewAllUnits")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : buildingResidents.length === 0 ? (
            <Card variant="glass" className="rounded-3xl">
              <CardContent className="p-8 text-center">
                <Search className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                <p className="text-sm text-gray-600 font-semibold mb-2">{t("hoa.buildingDetail.residents.noMatching")}</p>
                <p className="text-xs text-gray-500">
                  {t("hoa.buildingDetail.adjustSearch")}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {buildingResidents.map((resident) => {
                return (
                <Card key={resident.id} variant="glass" className="rounded-3xl ring-1 ring-black/5 shadow-lg shadow-black/10">
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="grid size-10 place-items-center rounded-2xl bg-white ring-1 ring-black/10 shadow-sm text-[#1F3A93] flex-shrink-0">
                          <Users className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[15px] font-semibold text-gray-900 truncate">
                            {resident.full_name}
                          </div>
                          <div className="text-xs text-gray-600 truncate">
                            {t("hoa.buildingDetail.unitLabel", { code: resident.unit_code })}
                          </div>
                          <div className="mt-2 space-y-1 text-sm">
                            <div className="text-gray-600 truncate">{resident.email}</div>
                            {resident.phone && (
                              <div className="text-gray-600 truncate">{resident.phone}</div>
                            )}
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <Badge className={
                                resident.is_primary
                                  ? "bg-emerald-100 text-emerald-700 ring-1 ring-black/10 border-0"
                                  : "bg-gray-100 text-gray-700 ring-1 ring-black/10 border-0"
                              }>
                                {getContactTypeLabel(resident.is_primary, t)}
                              </Badge>
                              <Badge className="bg-blue-100 text-blue-700 ring-1 ring-black/10 border-0">
                                {getResidentRoleLabel(resident.role, t)}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Actions */}
                      <div className="flex items-start gap-1.5 flex-shrink-0">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              className={`size-9 rounded-xl ${QUIET}`}
                              aria-label={t("hoa.buildingDetail.residents.actionsAria")}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48 rounded-xl">
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedResident(resident)
                                setShowViewResident(true)
                              }}
                              className="cursor-pointer"
                            >
                              <Eye className="h-4 w-4 mr-2" />{t("common.viewDetails")}</DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedResident(resident)
                                setShowEditResident(true)
                              }}
                              className="cursor-pointer"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              {t("hoa.buildingDetail.residents.modal.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedResident(resident)
                                setShowDeleteResident(true)
                              }}
                              className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t("hoa.buildingDetail.residents.modal.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                )
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(isOpen) => { if (!isOpen) setShowDeleteDialog(false) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hoa.buildingDetail.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("hoa.buildingDetail.deleteWarning")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMut.isPending ? t("hoa.buildingDetail.deleting") : t("hoa.buildingDetail.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Units CSV Import Modal */}
      <BulkImportModal
        open={showUnitsImport}
        onClose={() => setShowUnitsImport(false)}
        type="units"
        onImport={async (file) => {
          try {
            const result = await importUnitsMut.mutateAsync({ buildingId: id!, file });
            // DO NOT close modal here; let the modal show the success state
            return {
              success: result.success,
              total_processed: result.total_processed,
              success_count: result.success_count,
              error_count: result.error_count,
              errors: (result.errors || []).map((err: any) => ({
                row: err.row,
                field: err.field ?? "multiple",
                message: err.message ?? (Array.isArray(err.errors) ? err.errors.join(", ") : ""),
                data: err.data ?? {},
              })),
              created_unit_ids: result.created_unit_ids ?? [],
            };
          } catch (err: any) {
            // If backend sends JSON body on 400, surface it to the modal
            const data = err?.response?.data;
            if (data && typeof data === "object") {
              return {
                success: !!data.success,
                total_processed: Number(data.total_processed ?? 0),
                success_count: Number(data.success_count ?? 0),
                error_count: Number(data.error_count ?? 0),
                errors: (data.errors || []).map((e: any) => ({
                  row: e.row,
                  field: e.field ?? "multiple",
                  message: e.message ?? (Array.isArray(e.errors) ? e.errors.join(", ") : ""),
                  data: e.data ?? {},
                })),
                created_unit_ids: data.created_unit_ids ?? [],
              };
            }
            throw err; // let modal show a generic error if no structured body
          }
        }}
        onDownloadTemplate={downloadUnitTemplateMut.mutateAsync}
        importing={importUnitsMut.isPending}
      />

      {/* Residents CSV Import Modal */}
      <BulkImportModal
        open={showResidentsImport}
        onClose={() => setShowResidentsImport(false)}
        type="residents"
        onImport={async (file) => {
          try {
            const result = await importResidentsMut.mutateAsync({ buildingId: id!, file });
            // DO NOT close modal here
            return {
              success: result.success,
              total_processed: result.total_processed,
              success_count: result.success_count,
              error_count: result.error_count,
              errors: (result.errors || []).map((err: any) => ({
                row: err.row,
                field: err.field ?? "multiple",
                message: err.message ?? (Array.isArray(err.errors) ? err.errors.join(", ") : ""),
                data: err.data ?? {},
              })),
              created_resident_ids: result.created_resident_ids ?? [],
            };
          } catch (err: any) {
            const data = err?.response?.data;
            if (data && typeof data === "object") {
              return {
                success: !!data.success,
                total_processed: Number(data.total_processed ?? 0),
                success_count: Number(data.success_count ?? 0),
                error_count: Number(data.error_count ?? 0),
                errors: (data.errors || []).map((e: any) => ({
                  row: e.row,
                  field: e.field ?? "multiple",
                  message: e.message ?? (Array.isArray(e.errors) ? e.errors.join(", ") : ""),
                  data: e.data ?? {},
                })),
                created_resident_ids: data.created_resident_ids ?? [],
              };
            }
            throw err;
          }
        }}
        onDownloadTemplate={downloadResidentTemplateMut.mutateAsync}
        importing={importResidentsMut.isPending}
      />

      {/* Add Unit Modal */}
      <AddUnitModal
        open={showAddUnit}
        onClose={() => setShowAddUnit(false)}
        buildingId={id!}
        buildingName={building.name}
      />

      {/* Add Resident Modal */}
      <AddResidentModal
        open={showAddResident}
        onClose={() => setShowAddResident(false)}
        buildingId={id!}
        buildingName={building.name}
        vacantUnits={vacantUnits}
        onOpenAddUnit={handleOpenAddUnitFromResidentFlow}
        onGoToUnits={() => {
          setShowAddResident(false)
          setActiveTab("units")
        }}
      />

      {/* View Unit Modal */}
      {selectedUnit && (
        <ViewUnitModal
          open={showViewUnit}
          onClose={() => {
            setShowViewUnit(false)
            setSelectedUnit(null)
          }}
          unit={selectedUnit}
        />
      )}

      {/* Edit Unit Modal */}
      {selectedUnit && (
        <EditUnitModal
          open={showEditUnit}
          onClose={() => {
            setShowEditUnit(false)
            setSelectedUnit(null)
          }}
          unit={selectedUnit}
          buildingName={building.name}
        />
      )}

      {/* Delete Unit Confirmation */}
      {selectedUnit && (
        <DeleteUnitDialog
          open={showDeleteUnit}
          onClose={() => {
            setShowDeleteUnit(false)
            setSelectedUnit(null)
          }}
          unit={selectedUnit}
        />
      )}

      {/* View Resident Modal */}
      {selectedResident && (
        <ViewResidentModal
          open={showViewResident}
          onClose={() => {
            setShowViewResident(false)
            setSelectedResident(null)
          }}
          resident={selectedResident}
        />
      )}

      {/* Edit Resident Modal */}
      {selectedResident && (
        <EditResidentModal
          open={showEditResident}
          onClose={() => {
            setShowEditResident(false)
            setSelectedResident(null)
          }}
          resident={selectedResident}
          buildingId={id!}
          buildingName={building.name}
        />
      )}

      {/* Delete Resident Confirmation */}
      {selectedResident && (
        <DeleteResidentDialog
          open={showDeleteResident}
          onClose={() => {
            setShowDeleteResident(false)
            setSelectedResident(null)
          }}
          resident={selectedResident}
        />
      )}


    </div>
  )
}

/** =========================================================
 * Overview Tab Component
 * ========================================================= */
interface OverviewTabProps {
  building: Building
  stats?: BuildingStats
  isLoadingStats: boolean
  buildingPaymentConfig?: BuildingPaymentConfigResponse
  t: TranslateFn
  language: string
}

function OverviewTab({
  building,
  stats,
  isLoadingStats,
  buildingPaymentConfig,
  t,
  language,
}: OverviewTabProps) {
  const paymentStatus =
    buildingPaymentConfig?.payment_config_status ?? building.payment_config_status ?? "MISSING"
  const paymentStatusClass =
    paymentStatus === "CONFIGURED"
      ? "bg-emerald-100 text-emerald-700"
      : paymentStatus === "HOA_DEFAULT"
        ? "bg-amber-100 text-amber-700"
        : "bg-rose-100 text-rose-700"
  const paymentSource = buildingPaymentConfig?.config ?? buildingPaymentConfig?.fallback_payment_details

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {isLoadingStats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
          <Skeleton className="h-32 rounded-2xl" />
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            icon={Home}
            label={t("hoa.buildingDetail.stats.totalUnits")}
            value={stats.total_units.toString()}
            subValue={t("hoa.buildingDetail.stats.occupiedSub", { count: stats.occupied_units })}
            tone="blue"
          />
          <StatCard
            icon={Users}
            label={t("hoa.buildingDetail.stats.totalResidents")}
            value={stats.total_residents.toString()}
            subValue={t("hoa.buildingDetail.stats.primarySub", { count: stats.primary_residents })}
            tone="purple"
          />
          <StatCard
            icon={TrendingUp}
            label={t("hoa.buildingDetail.stats.occupancyRate")}
            value={`${stats.occupancy_rate}%`}
            tone={stats.occupancy_rate > 80 ? "green" : stats.occupancy_rate > 50 ? "amber" : "red"}
          />
          <StatCard
            icon={DollarSign}
            label={t("hoa.buildingDetail.stats.avgMonthlyFee")}
            value={formatCurrencyValue(stats.average_monthly_fee, language, 0, 0)}
            tone="green"
          />
        </div>
      ) : null}

      {/* Detailed Stats Grid */}
      {stats && (
        <Card variant="glass" className="rounded-3xl">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              {t("hoa.buildingDetail.stats.detailed")}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <DetailItem label={t("hoa.buildingDetail.stats.occupiedUnits")} value={stats.occupied_units.toString()} />
              <DetailItem label={t("hoa.buildingDetail.stats.vacantUnits")} value={stats.vacant_units.toString()} />
              <DetailItem label={t("hoa.buildingDetail.stats.inactiveUnits")} value={stats.inactive_units.toString()} />
              <DetailItem label={t("hoa.buildingDetail.stats.primaryResidents")} value={stats.primary_residents.toString()} />
              <DetailItem label={t("hoa.buildingDetail.stats.secondaryResidents")} value={stats.secondary_residents.toString()} />
              <DetailItem
                label={t("hoa.buildingDetail.stats.totalBalance")}
                value={formatCurrencyValue(stats.total_balance, language)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Building Information */}
      <Card variant="glass" className="rounded-3xl">
        <CardContent className="p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            {t("hoa.buildingDetail.info.title")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <DetailItem label={t("hoa.buildingDetail.info.buildingCode")} value={building.code} />
              <DetailItem label={t("hoa.buildingDetail.info.buildingName")} value={building.name} />
              <DetailItem label={t("buildings.rif")} value={building.rif || "—"} />
              <DetailItem
                label={t("common.status")}
                value={
                  building.is_active ? (
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                      <CheckCircle2 className="h-3 w-3 mr-1" />{t("hoa.buildingDetail.status.active")}</Badge>
                  ) : (
                    <Badge variant="secondary">
                      <XCircle className="h-3 w-3 mr-1" />{t("hoa.buildingDetail.status.inactive")}</Badge>
                  )
                }
              />
              <DetailItem label={t("hoa.buildingDetail.info.totalUnitsPlanned")} value={building.total_units.toString()} />
              <DetailItem label={t("hoa.buildingDetail.info.totalFloors")} value={building.floors.toString()} />
              <DetailItem
                label={t("hoa.buildingDetail.info.defaultMonthlyFee")}
                value={formatCurrencyValue(building.default_monthly_fee, language)}
              />
            </div>
            <div className="space-y-4">
              <DetailItem label={t("hoa.buildingDetail.info.address")} value={building.address || "—"} />
              <DetailItem label={t("hoa.buildingDetail.info.city")} value={building.city || "—"} />
              <DetailItem label={t("hoa.buildingDetail.info.state")} value={building.state || "—"} />
              <DetailItem label={t("hoa.buildingDetail.info.postalCode")} value={building.postal_code || "—"} />
              <DetailItem label={t("hoa.buildingDetail.info.country")} value={building.country || "—"} />
            </div>
          </div>
          {building.notes && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <DetailItem
                label={t("hoa.buildingDetail.notes")}
                value={
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{building.notes}</p>
                }
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Card variant="glass" className="rounded-3xl">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Payment Routing Destination
            </h3>
            <Badge className={`text-xs ${paymentStatusClass}`}>
              {paymentStatus === "CONFIGURED"
                ? "Building configured"
                : paymentStatus === "HOA_DEFAULT"
                  ? "Using HOA default"
                  : "Missing destination"}
            </Badge>
          </div>

          {paymentSource ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailItem label={t("settings.bankName")} value={paymentSource.bank_name || "-"} />
              <DetailItem label={t("settings.accountHolderName")} value={paymentSource.account_holder_name || "-"} />
              <DetailItem label={t("settings.accountNumber")} value={paymentSource.bank_account_number || "-"} />
              <DetailItem label={t("settings.routingNumber")} value={paymentSource.bank_routing_number || "-"} />
              <DetailItem label={t("settings.zelleEmail")} value={paymentSource.zelle_email || "-"} />
              <DetailItem label={t("settings.zellePhone")} value={paymentSource.zelle_phone || "-"} />
              <DetailItem label={t("settings.paypalEmail")} value={paymentSource.paypal_email || "-"} />
              <DetailItem label={t("settings.bank")} value={paymentSource.pago_movil_bank || "-"} />
              <DetailItem label={t("settings.phone")} value={paymentSource.pago_movil_phone || "-"} />
              <DetailItem label={t("settings.idNumber")} value={paymentSource.pago_movil_id_number || "-"} />
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              No payment destination has been configured for this building yet.
            </p>
          )}

          {paymentSource?.cash_instructions && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
                {t("settings.cashInstructionsTitle")}
              </p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{paymentSource.cash_instructions}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

/** =========================================================
 * Stat Card Component
 * ========================================================= */
interface StatCardProps {
  icon: any
  label: string
  value: string
  subValue?: string
  tone?: "green" | "amber" | "red" | "blue" | "purple"
}

function StatCard({ icon: Icon, label, value, subValue, tone = "blue" }: StatCardProps) {
  const bgGradient = {
    green: "from-green-50/80 to-green-100/50",
    amber: "from-amber-50/80 to-amber-100/50",
    red: "from-red-50/80 to-red-100/50",
    blue: "from-blue-50/80 to-blue-100/50",
    purple: "from-purple-50/80 to-purple-100/50",
  }[tone]

  const textColor = {
    green: "text-green-700",
    amber: "text-amber-700",
    red: "text-red-700",
    blue: "text-blue-700",
    purple: "text-purple-700",
  }[tone]

  const iconColor = {
    green: "text-green-600",
    amber: "text-amber-600",
    red: "text-red-600",
    blue: "text-blue-600",
    purple: "text-purple-600",
  }[tone]

  return (
    <Card variant="glass" className="rounded-2xl overflow-hidden">
      <CardContent className={`p-5 bg-gradient-to-br ${bgGradient}`}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-gray-600 font-medium uppercase tracking-wide">
            {label}
          </span>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className={`text-2xl font-bold ${textColor} mb-1`}>{value}</div>
        {subValue && <div className="text-xs text-gray-600">{subValue}</div>}
      </CardContent>
    </Card>
  )
}

/** =========================================================
 * Detail Item Component
 * ========================================================= */
interface DetailItemProps {
  label: string
  value: React.ReactNode
}

function DetailItem({ label, value }: DetailItemProps) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</div>
      <div className="text-sm text-gray-900 font-medium">
        {typeof value === "string" ? value : value}
      </div>
    </div>
  )
}

/** =========================================================
 * Skeleton Component
 * ========================================================= */
function BuildingDetailSkeleton() {
  return (
    <div className="space-y-5 relative">
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(1200px_600px_at_50%_-10%,rgba(31,58,147,0.06),transparent_60%)]" />
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 space-y-3">
          <Skeleton className="h-8 w-32 rounded" />
          <Skeleton className="h-8 w-64 rounded" />
          <Skeleton className="h-5 w-48 rounded" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-24 rounded-xl" />
          <Skeleton className="h-10 w-24 rounded-xl" />
        </div>
      </div>
      <Skeleton className="h-11 w-96 rounded-2xl" />
      <div className="grid grid-cols-4 gap-4">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-32 rounded-2xl" />
      </div>
      <Skeleton className="h-96 rounded-3xl" />
    </div>
  )
}

/** =========================================================
 * Add Unit Modal Component
 * ========================================================= */
interface AddUnitModalProps {
  open: boolean
  onClose: () => void
  buildingId: string
  buildingName: string
}

function AddUnitModal({ open, onClose, buildingId, buildingName }: AddUnitModalProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = React.useState({
    unit_code: "",
    floor: "",
    number: "",
    monthly_fee: "",
    alicuota: "",
    status: "vacant" as UnitStatus,
  })

  const createUnitMut = useCreateUnit()

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const unitData: CreateUnitData = {
        unit_code: formData.unit_code,
        building: buildingId,  // Send building (FK to Building/Condo model)
        floor: formData.floor || undefined,
        number: formData.number || undefined,
        monthly_fee: parseFloat(formData.monthly_fee),
        alicuota: formData.alicuota ? parseFloat(formData.alicuota) : undefined,
        status: formData.status,
      }

      await createUnitMut.mutateAsync(unitData)

      toast.success(t("hoa.buildingDetail.toast.unitCreated"), {
        description: t("hoa.buildingDetail.toast.unitCreatedDescription", {
          code: formData.unit_code,
          building: buildingName,
        }),
      })

      // Reset form and close
      setFormData({
        unit_code: "",
        floor: "",
        number: "",
        monthly_fee: "",
        alicuota: "",
        status: "vacant",
      })
      // Defer closing to avoid portal unmount race conditions
      setTimeout(() => {
        onClose()
      }, 0)
    } catch (error: any) {
      console.error("Failed to create unit:", error)

      // Extract error message from various possible formats
      let errorMessage = t("error.generic")
      if (error.response?.data) {
        const data = error.response.data
        // Check for different error formats
        if (data.detail) {
          errorMessage = data.detail
        } else if (data.unit_code) {
          // Field-specific errors
          errorMessage = Array.isArray(data.unit_code) ? data.unit_code[0] : data.unit_code
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors
        } else if (typeof data === 'string') {
          errorMessage = data
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      // Check for duplicate key error specifically
      if (errorMessage.toLowerCase().includes('duplicate') || errorMessage.toLowerCase().includes('already exists')) {
        errorMessage = t("hoa.buildingDetail.toast.unitDuplicate", { code: formData.unit_code })
      }

      toast.error(t("hoa.buildingDetail.toast.unitCreateFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.units.modal.create")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.units.modal.createDescription")} <strong>{buildingName}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto px-1 -mx-1 space-y-4 py-4" style={{ maxHeight: "calc(85vh - 180px)" }}>
            <div className="space-y-2">
              <Label htmlFor="unit_code">
                {t("hoa.buildingDetail.units.form.unitCode")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="unit_code"
                value={formData.unit_code}
                onChange={(e) => handleChange("unit_code", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.unitCodePlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="floor">{t("hoa.buildingDetail.units.form.floor")}</Label>
                <Input
                  id="floor"
                  value={formData.floor}
                  onChange={(e) => handleChange("floor", e.target.value)}
                  placeholder={t("hoa.buildingDetail.units.form.floorPlaceholder")}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="number">{t("hoa.buildingDetail.units.form.unitNumber")}</Label>
                <Input
                  id="number"
                  value={formData.number}
                  onChange={(e) => handleChange("number", e.target.value)}
                  placeholder={t("hoa.buildingDetail.units.form.unitNumberPlaceholder")}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthly_fee">
                {t("hoa.buildingDetail.units.form.monthlyFee")} ($) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="monthly_fee"
                type="number"
                step="0.01"
                min="0"
                value={formData.monthly_fee}
                onChange={(e) => handleChange("monthly_fee", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.monthlyFeePlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="alicuota">{t("hoa.buildingDetail.units.form.alicuota")}</Label>
              <Input
                id="alicuota"
                type="number"
                step="0.0001"
                min="0"
                max="100"
                value={formData.alicuota}
                onChange={(e) => handleChange("alicuota", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.alicuotaPlaceholder")}
                className="rounded-xl"
              />
              <p className="text-xs text-gray-500">{t("hoa.buildingDetail.units.form.alicuotaHelper")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">{t("common.status")}</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("hoa.buildingDetail.units.form.selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="occupied">{t("hoa.buildingDetail.units.status.occupied")}</SelectItem>
                  <SelectItem value="vacant">{t("hoa.buildingDetail.units.status.vacant")}</SelectItem>
                  <SelectItem value="inactive">{t("hoa.buildingDetail.units.status.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createUnitMut.isPending}
              className={QUIET}
            >{t("common.cancel")}</Button>
            <Button
              type="submit"
              disabled={createUnitMut.isPending}
              style={{ backgroundColor: PRIMARY }}
              className="text-white hover:opacity-90 rounded-xl shadow-lg hover:shadow-xl"
            >
              {createUnitMut.isPending ? t("hoa.buildingDetail.units.form.creating") : t("hoa.buildingDetail.createUnit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Add Resident Modal Component
 * ========================================================= */
interface AddResidentModalProps {
  open: boolean
  onClose: () => void
  buildingId: string
  buildingName: string
  vacantUnits: UnitList[]
  onOpenAddUnit: () => void
  onGoToUnits: () => void
}

function AddResidentModal({ open, onClose, buildingId, buildingName, vacantUnits, onOpenAddUnit, onGoToUnits }: AddResidentModalProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = React.useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    cedula: "",
    unit_id: "",
    resident_type: "owner" as ResidentRole,
    is_primary_contact: true,
  })
  const [idDocFile, setIdDocFile] = React.useState<File | null>(null)
  const [idDocPreview, setIdDocPreview] = React.useState<string | null>(null)
  const [idDocError, setIdDocError] = React.useState<string | null>(null)
  const idDocInputRef = React.useRef<HTMLInputElement>(null)

  function handleIdDocChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    if (!file) {
      setIdDocFile(null)
      setIdDocError(null)
      return
    }

    const validationMessage = getResidentIdDocValidationMessage(file, t)
    if (validationMessage) {
      setIdDocFile(null)
      setIdDocError(validationMessage)
      if (idDocInputRef.current) idDocInputRef.current.value = ""
      return
    }

    setIdDocFile(file)
    setIdDocError(null)
    setIdDocPreview(URL.createObjectURL(file))
  }

  const createResidentMut = useCreateResident()
  const { data: availableUnits = [] } = useUnitsByBuilding(buildingId)

  const baseUnits = vacantUnits.length > 0 ? vacantUnits : availableUnits
  const vacantOptions = React.useMemo(
    () => baseUnits.filter((u) => {
      // Allow assignment to:
      // 1. Units with vacant status and no residents
      // 2. Units marked as occupied but have no residents (data inconsistency case)
      // Do NOT allow assignment to inactive units
      return u.status !== "inactive" && u.resident_count === 0
    }),
    [baseUnits]
  )
  const hasVacantOptions = vacantOptions.length > 0

  React.useEffect(() => {
    if (!formData.unit_id && hasVacantOptions) {
      setFormData((prev) => ({ ...prev, unit_id: String(vacantOptions[0].id) }))
    }
  }, [hasVacantOptions, vacantOptions, formData.unit_id])

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (idDocError) {
      toast.error(idDocError)
      return
    }

    if (!formData.unit_id) {
      toast.error(hasVacantOptions ? t("hoa.buildingDetail.residents.selectVacantUnit") : t("hoa.buildingDetail.residents.noUnitsAvailable"), {
        action: {
          label: t("hoa.buildingDetail.createUnit"),
          onClick: () => onOpenAddUnit(),
        },
      })
      return
    }

    try {
      const residentData: CreateResidentData = {
        full_name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone: formData.phone || undefined,
        cedula: formData.cedula || undefined,
        id_document_image: idDocFile || undefined,
        unit: formData.unit_id,
        role: formData.resident_type,
        is_primary: formData.is_primary_contact,
      }

      await createResidentMut.mutateAsync(residentData)

      toast.success(t("hoa.buildingDetail.toast.residentCreated"), {
        description: t("hoa.buildingDetail.toast.residentCreatedDescription", {
          name: `${formData.first_name} ${formData.last_name}`,
          building: buildingName,
        }),
      })

      // Reset form and close
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        cedula: "",
        unit_id: "",
        resident_type: "owner",
        is_primary_contact: true,
      })
      setIdDocFile(null)
      setIdDocPreview(null)
      setIdDocError(null)
      // Defer closing to avoid portal unmount race conditions
      setTimeout(() => {
        onClose()
      }, 0)
    } catch (error: any) {
      console.error("Failed to create resident:", error)

      let errorMessage = t("error.generic")
      if (error.response?.data) {
        const data = error.response.data
        if (data.detail) {
          errorMessage = data.detail
        } else if (data.id_document_image) {
          errorMessage = Array.isArray(data.id_document_image)
            ? data.id_document_image[0]
            : data.id_document_image
        } else if (data.email) {
          errorMessage = Array.isArray(data.email) ? data.email[0] : data.email
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors)
            ? data.non_field_errors[0]
            : data.non_field_errors
        } else if (typeof data === "string") {
          errorMessage = data
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      if (
        errorMessage.toLowerCase().includes("duplicate") ||
        errorMessage.toLowerCase().includes("already exists")
      ) {
        errorMessage = t("hoa.buildingDetail.toast.residentDuplicate", { email: formData.email })
      }

      toast.error(t("hoa.buildingDetail.toast.residentCreateFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.residents.modal.create")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.residents.modal.createDescription")} <strong>{buildingName}</strong>
          </DialogDescription>
        </DialogHeader>

        {!hasVacantOptions && (
          <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100 text-amber-950 px-5 py-4 space-y-3 shadow-sm">
            <div className="flex items-center gap-2">
              <svg className="h-5 w-5 text-amber-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="font-bold text-base">{t("hoa.buildingDetail.cannotCreateResident")}</div>
            </div>
            <div className="text-sm leading-relaxed">
              <strong>{t("hoa.buildingDetail.noVacantUnits")}</strong> {t("hoa.buildingDetail.noVacantUnitsDescriptionShort")}
            </div>
            <div className="text-sm font-medium bg-amber-50 -mx-2 px-2 py-2 rounded-lg border border-amber-200">
              {t("hoa.buildingDetail.createNewVacantUnit")}<br/>
              {t("hoa.buildingDetail.markExistingUnitVacant")}
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={onOpenAddUnit} className="font-medium">{t("hoa.buildingDetail.createUnit")}</Button>
              <Button size="sm" variant="outline" className={QUIET} onClick={onGoToUnits}>{t("hoa.buildingDetail.viewAllUnits")}</Button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div
            className="overflow-y-auto px-1 -mx-1 space-y-4 py-4"
            style={{ maxHeight: "calc(85vh - 180px)" }}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">
                  {t("hoa.buildingDetail.residents.form.firstName")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder={t("hoa.buildingDetail.residents.form.firstNamePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">
                  {t("hoa.buildingDetail.residents.form.lastName")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder={t("hoa.buildingDetail.residents.form.lastNamePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("hoa.buildingDetail.residents.form.email")}<span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t("hoa.buildingDetail.residents.form.emailPlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t("hoa.buildingDetail.residents.form.phone")}</Label>
              <PhoneInputField
                id="phone"
                value={formData.phone}
                onChange={(val) => handleChange("phone", val || "")}
                placeholder={t("hoa.buildingDetail.residents.form.phonePlaceholder")}
              />
            </div>

            {/* Identity fields */}
            <div className="space-y-2">
              <Label htmlFor="cedula">{t("hoa.buildingDetail.residents.form.identityDocument")}</Label>
              <Input
                id="cedula"
                value={formData.cedula}
                onChange={(e) => handleChange("cedula", e.target.value)}
                placeholder={t("hoa.buildingDetail.residents.form.identityPlaceholder")}
                className="rounded-xl"
              />
              <p className="text-xs text-gray-400">{t("hoa.buildingDetail.residents.form.identityHelper")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="id_doc">{t("hoa.buildingDetail.residents.form.idDocumentPhoto")} <span className="text-gray-400 text-xs font-normal">({t("hoa.buildingDetail.optional")})</span></Label>
              <p className="text-xs text-gray-400">{t("hoa.buildingDetail.residents.form.idDocumentUploadHelper")}</p>
              {idDocPreview && (
                <div className="relative inline-block">
                  <img src={idDocPreview} alt={t("hoa.buildingDetail.residents.form.idPreviewAlt")} className="h-24 w-auto rounded-xl object-contain border border-black/10 bg-gray-50" />
                  <button
                    type="button"
                    onClick={() => {
                      setIdDocFile(null)
                      setIdDocPreview(null)
                      setIdDocError(null)
                      if (idDocInputRef.current) idDocInputRef.current.value = ""
                    }}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center shadow ring-2 ring-white"
                  >✕</button>
                </div>
              )}
              <Input ref={idDocInputRef} id="id_doc" type="file" accept="image/*" onChange={handleIdDocChange} className="rounded-xl text-sm" />
              {idDocError && <p className="text-xs text-rose-600">{idDocError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_id">{t("hoa.buildingDetail.residents.form.unit")}<span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.unit_id}
                onValueChange={(value) => handleChange("unit_id", value)}
                disabled={!hasVacantOptions}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={hasVacantOptions ? t("hoa.buildingDetail.residents.form.selectVacantUnit") : t("hoa.buildingDetail.noVacantUnits")} />
                </SelectTrigger>

                <SelectContent>
                  {hasVacantOptions && vacantOptions.map((unit) => (
                    <SelectItem key={unit.id} value={String(unit.id)}>
                      {unit.unit_code} {unit.floor && `- ${t("hoa.buildingDetail.floorLabel", { floor: unit.floor })}`} - {t("hoa.buildingDetail.units.status.vacant")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!hasVacantOptions && (
                <div className="flex items-center gap-1.5 text-xs text-amber-800 mt-1 bg-amber-50 px-2 py-1.5 rounded-md border border-amber-200">
                  <svg className="h-3.5 w-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span className="font-medium">{t("hoa.buildingDetail.createUnitFirst")}</span>
                </div>
              )}
            </div>
            {/* ✅ End of Fixed Select Section */}

            <div className="space-y-2">
              <Label htmlFor="resident_type">{t("hoa.buildingDetail.residents.form.role")}</Label>
              <Select
                value={formData.resident_type}
                onValueChange={(value) => handleChange("resident_type", value)}
              >
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("hoa.buildingDetail.residents.form.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">{t("hoa.buildingDetail.residents.role.owner")}</SelectItem>
                  <SelectItem value="tenant">{t("hoa.buildingDetail.residents.role.tenant")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
              <div className="space-y-0.5">
                <Label htmlFor="is_primary_contact" className="text-sm font-medium">
                  {t("hoa.buildingDetail.residents.form.primaryContact")}
                </Label>
                <p className="text-xs text-gray-500">{t("hoa.buildingDetail.residents.form.primaryContactHelper")}</p>
              </div>
              <Switch
                id="is_primary_contact"
                checked={formData.is_primary_contact}
                onCheckedChange={(checked) =>
                  handleChange("is_primary_contact", checked)
                }
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={createResidentMut.isPending}
              className={QUIET}
            >{t("common.cancel")}</Button>
            <Button
              type="submit"
              disabled={createResidentMut.isPending || !hasVacantOptions}
              style={{ backgroundColor: PRIMARY }}
              className="text-white hover:opacity-90 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createResidentMut.isPending
                ? t("hoa.buildingDetail.residents.form.creating")
                : !hasVacantOptions
                  ? t("hoa.buildingDetail.noVacantUnits")
                  : t("hoa.buildingDetail.residents.modal.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}


/** =========================================================
 * View Unit Modal Component
 * ========================================================= */
interface ViewUnitModalProps {
  open: boolean
  onClose: () => void
  unit: UnitList
}

function ViewUnitModal({ open, onClose, unit }: ViewUnitModalProps) {
  const { t, language } = useLanguage();
  const unitDetailQ = useUnit(unit.id)
  const unitDetail = unitDetailQ.data

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.units.detailsTitle")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.units.viewingDetailsFor", { code: unit.unit_code })}
          </DialogDescription>
        </DialogHeader>

        {unitDetailQ.isLoading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        ) : unitDetail ? (
          <div className="overflow-y-auto px-1 -mx-1 py-4" style={{ maxHeight: "calc(85vh - 180px)" }}>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.sections.basicInformation")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={t("hoa.buildingDetail.units.form.unitCode")} value={unitDetail.unit_code} />
                  <DetailItem label={t("common.status")} value={
                    <Badge className={
                      unitDetail.status === "occupied"
                        ? "bg-green-100 text-green-700 border-0"
                        : unitDetail.status === "vacant"
                          ? "bg-blue-100 text-blue-700 border-0"
                          : "bg-gray-100 text-gray-700 border-0"
                    }>
                        {getUnitStatusLabel(unitDetail.status, t)}
                    </Badge>
                  } />
                    <DetailItem label={t("hoa.buildingDetail.units.form.floor")} value={unitDetail.floor || "—"} />
                    <DetailItem label={t("hoa.buildingDetail.units.form.unitNumber")} value={unitDetail.number || "—"} />
                    <DetailItem label={t("hoa.buildingDetail.info.buildingName")} value={unitDetail.building_name || "—"} />
                    <DetailItem label={t("hoa.buildingDetail.info.buildingCode")} value={unitDetail.building_code || "—"} />
                </div>
              </div>

              {/* Financial Info */}
              <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.sections.financialInformation")}</h3>
                <div className="grid grid-cols-2 gap-4">
                    <DetailItem label={t("hoa.buildingDetail.units.form.monthlyFee")} value={formatCurrencyValue(unitDetail.monthly_fee, language)} />
                    <DetailItem label={t("hoa.buildingDetail.financial.currentBalance")} value={formatCurrencyValue(unitDetail.current_balance, language)} />
                    <DetailItem label={t("hoa.buildingDetail.financial.initialBalance")} value={formatCurrencyValue(unitDetail.initial_balance, language)} />
                </div>
              </div>

              {/* Timestamps */}
              <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.sections.systemInformation")}</h3>
                <div className="grid grid-cols-2 gap-4">
                    <DetailItem label={t("hoa.buildingDetail.info.created")} value={formatDateValue(unitDetail.created_at, language)} />
                    <DetailItem label={t("hoa.buildingDetail.info.lastUpdated")} value={formatDateValue(unitDetail.updated_at, language)} />
                </div>
              </div>
            </div>
          </div>
        ) : (
            <div className="py-8 text-center text-gray-500">{t("hoa.buildingDetail.units.loadDetailsFailed")}</div>
        )}

        <DialogFooter>
            <Button onClick={onClose} className={QUIET}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Edit Unit Modal Component
 * ========================================================= */
interface EditUnitModalProps {
  open: boolean
  onClose: () => void
  unit: UnitList
  buildingName: string
}

function EditUnitModal({ open, onClose, unit, buildingName }: EditUnitModalProps) {
  const { t } = useLanguage();
  const [formData, setFormData] = React.useState({
    unit_code: unit.unit_code,
    floor: unit.floor || "",
    number: unit.number || "",
    monthly_fee: unit.monthly_fee,
    alicuota: unit.alicuota || "0",
    status: unit.status as UnitStatus,
  })

  const updateUnitMut = useUpdateUnit()

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const updateData: UpdateUnitData = {
        unit_code: formData.unit_code,
        floor: formData.floor || undefined,
        number: formData.number || undefined,
        monthly_fee: parseFloat(formData.monthly_fee),
        alicuota: parseFloat(formData.alicuota) || 0,
        status: formData.status,
      }

      await updateUnitMut.mutateAsync({ id: unit.id, data: updateData })

      toast.success(t("hoa.buildingDetail.toast.unitUpdated"), {
        description: t("hoa.buildingDetail.toast.unitUpdatedDescription", { code: formData.unit_code }),
      })

      onClose()
    } catch (error: any) {
      console.error("Failed to update unit:", error)

      let errorMessage = t("error.generic")
      if (error.response?.data) {
        const data = error.response.data
        if (data.detail) {
          errorMessage = data.detail
        } else if (data.unit_code) {
          errorMessage = Array.isArray(data.unit_code) ? data.unit_code[0] : data.unit_code
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors
        } else if (typeof data === 'string') {
          errorMessage = data
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(t("hoa.buildingDetail.toast.unitUpdateFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.units.modal.edit")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.units.modal.editDescription")} <strong>{buildingName}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto px-1 -mx-1 space-y-4 py-4" style={{ maxHeight: "calc(85vh - 180px)" }}>
            <div className="space-y-2">
              <Label htmlFor="edit_unit_code">
                {t("hoa.buildingDetail.units.form.unitCode")} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit_unit_code"
                value={formData.unit_code}
                onChange={(e) => handleChange("unit_code", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.unitCodePlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_floor">{t("hoa.buildingDetail.units.form.floor")}</Label>
                <Input
                  id="edit_floor"
                  value={formData.floor}
                  onChange={(e) => handleChange("floor", e.target.value)}
                  placeholder={t("hoa.buildingDetail.units.form.floorPlaceholder")}
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_number">{t("hoa.buildingDetail.units.form.unitNumber")}</Label>
                <Input
                  id="edit_number"
                  value={formData.number}
                  onChange={(e) => handleChange("number", e.target.value)}
                  placeholder={t("hoa.buildingDetail.units.form.unitNumberPlaceholder")}
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_monthly_fee">
                {t("hoa.buildingDetail.units.form.monthlyFee")} ($) <span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit_monthly_fee"
                type="number"
                step="0.01"
                min="0"
                value={formData.monthly_fee}
                onChange={(e) => handleChange("monthly_fee", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.monthlyFeePlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_alicuota">{t("hoa.buildingDetail.units.form.alicuota")}</Label>
              <Input
                id="edit_alicuota"
                type="number"
                step="0.0001"
                min="0"
                max="100"
                value={formData.alicuota}
                onChange={(e) => handleChange("alicuota", e.target.value)}
                placeholder={t("hoa.buildingDetail.units.form.alicuotaPlaceholder")}
                className="rounded-xl"
              />
              <p className="text-xs text-gray-500">{t("hoa.buildingDetail.units.form.alicuotaHelper")}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_status">{t("common.status")}</Label>
              <Select value={formData.status} onValueChange={(value) => handleChange("status", value)}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("hoa.buildingDetail.units.form.selectStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="occupied">{t("hoa.buildingDetail.units.status.occupied")}</SelectItem>
                  <SelectItem value="vacant">{t("hoa.buildingDetail.units.status.vacant")}</SelectItem>
                  <SelectItem value="inactive">{t("hoa.buildingDetail.units.status.inactive")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateUnitMut.isPending}
              className={QUIET}
            >{t("common.cancel")}</Button>
            <Button
              type="submit"
              disabled={updateUnitMut.isPending}
              style={{ backgroundColor: PRIMARY }}
              className="text-white hover:opacity-90 rounded-xl shadow-lg hover:shadow-xl"
            >
              {updateUnitMut.isPending ? t("hoa.buildingDetail.units.form.updating") : t("hoa.buildingDetail.units.updateUnit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Delete Unit Dialog Component
 * ========================================================= */
interface DeleteUnitDialogProps {
  open: boolean
  onClose: () => void
  unit: UnitList
}

function DeleteUnitDialog({ open, onClose, unit }: DeleteUnitDialogProps) {
  const { t } = useLanguage();
  const deleteUnitMut = useDeleteUnit()

  const handleDelete = async () => {
    try {
      await deleteUnitMut.mutateAsync(unit.id)

      toast.success(t("hoa.buildingDetail.toast.unitDeleted"), {
        description: t("hoa.buildingDetail.toast.unitDeletedDescription", { code: unit.unit_code }),
      })

      onClose()
    } catch (error: any) {
      console.error("Failed to delete unit:", error)

      let errorMessage = t("hoa.buildingDetail.toast.unitDeleteErrorDescription")
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(t("hoa.buildingDetail.toast.unitDeleteFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("hoa.buildingDetail.units.deleteTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("hoa.buildingDetail.units.deleteDescription", { code: unit.unit_code })}
            {unit.resident_count > 0 && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm font-medium">
                  {t("hoa.buildingDetail.units.deleteResidentWarning", { count: unit.resident_count })}
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteUnitMut.isPending}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteUnitMut.isPending}
          >
            {deleteUnitMut.isPending ? t("hoa.buildingDetail.deleting") : t("hoa.buildingDetail.units.modal.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/** =========================================================
 * View Resident Modal Component
 * ========================================================= */
interface ViewResidentModalProps {
  open: boolean
  onClose: () => void
  resident: ResidentList
}

function ViewResidentModal({ open, onClose, resident }: ViewResidentModalProps) {
  const { t, language } = useLanguage();
  const residentDetailQ = useResident(resident.id)
  const residentDetail = residentDetailQ.data

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.residents.detailsTitle")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.residents.viewingDetailsFor", { name: resident.full_name })}
          </DialogDescription>
        </DialogHeader>

        {residentDetailQ.isLoading ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
            <Skeleton className="h-16 rounded-xl" />
          </div>
        ) : residentDetail ? (
          <div className="overflow-y-auto px-1 -mx-1 py-4" style={{ maxHeight: "calc(85vh - 180px)" }}>
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.sections.basicInformation")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={t("hoa.buildingDetail.residents.fullName")} value={residentDetail.full_name} />
                  <DetailItem label={t("hoa.buildingDetail.residents.form.email")} value={residentDetail.email} />
                  <DetailItem label={t("hoa.buildingDetail.residents.form.phone")} value={residentDetail.phone || "—"} />
                  <DetailItem label={t("hoa.buildingDetail.residents.language")} value={residentDetail.language || "—"} />
                </div>
              </div>

              {/* Unit Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.residents.unitInformation")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={t("hoa.buildingDetail.units.form.unitCode")} value={residentDetail.unit_details?.unit_code || "—"} />
                  <DetailItem label={t("hoa.buildingDetail.info.buildingName")} value={residentDetail.unit_details?.building_name || "—"} />
                  <DetailItem label={t("hoa.buildingDetail.units.form.floor")} value={residentDetail.unit_details?.floor || "—"} />
                  <DetailItem label={t("hoa.buildingDetail.units.form.monthlyFee")} value={residentDetail.unit_details ? formatCurrencyValue(residentDetail.unit_details.monthly_fee, language) : "—"} />
                </div>
              </div>

              {/* Resident Type */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.residents.typeTitle")}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <DetailItem label={t("hoa.buildingDetail.residents.form.role")} value={
                    <Badge className="bg-blue-100 text-blue-700 border-0">
                      {getResidentRoleLabel(residentDetail.role, t)}
                    </Badge>
                  } />
                  <DetailItem label={t("hoa.buildingDetail.residents.form.contactType")} value={
                    <Badge className={
                      residentDetail.is_primary
                        ? "bg-emerald-100 text-emerald-700 border-0"
                        : "bg-gray-100 text-gray-700 border-0"
                    }>
                      {getContactTypeLabel(residentDetail.is_primary, t)}
                    </Badge>
                  } />
                </div>
              </div>

              {/* Identity */}
              {(residentDetail.cedula || residentDetail.id_document_image) && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t("hoa.buildingDetail.residents.identityTitle")}</h3>
                  {residentDetail.cedula && (
                    <DetailItem label={t("hoa.buildingDetail.residents.form.identityDocument")} value={residentDetail.cedula} />
                  )}
                  {residentDetail.id_document_image && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1.5">{t("hoa.buildingDetail.residents.idDocument")}</p>
                      {/\.(jpe?g|png|gif|webp|bmp)$/i.test(residentDetail.id_document_image) ? (
                        <a href={residentDetail.id_document_image} target="_blank" rel="noopener noreferrer">
                          <img
                            src={residentDetail.id_document_image}
                            alt={t("hoa.buildingDetail.residents.idDocument")}
                            className="h-32 w-auto rounded-xl object-contain border border-black/10 bg-gray-50 ring-1 ring-black/5 hover:ring-2 hover:ring-blue-400 transition-all"
                          />
                        </a>
                      ) : (
                        <a
                          href={residentDetail.id_document_image}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 rounded-xl border border-black/10 bg-gray-50 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 ring-1 ring-black/5"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                          {t("hoa.buildingDetail.residents.viewDocument")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">{t("hoa.buildingDetail.residents.loadDetailsFailed")}</div>
        )}

        <DialogFooter>
          <Button onClick={onClose} className={QUIET}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Edit Resident Modal Component
 * ========================================================= */
interface EditResidentModalProps {
  open: boolean
  onClose: () => void
  resident: ResidentList
  buildingId: string
  buildingName: string
}

function EditResidentModal({ open, onClose, resident, buildingId, buildingName }: EditResidentModalProps) {
  const { t } = useLanguage();
  // Parse full_name into first and last name
  const nameParts = resident.full_name.split(" ")
  const firstName = nameParts[0] || ""
  const lastName = nameParts.slice(1).join(" ") || ""

  const [formData, setFormData] = React.useState({
    first_name: firstName,
    last_name: lastName,
    email: resident.email,
    phone: resident.phone || "",
    cedula: resident.cedula || "",
    resident_type: resident.role as ResidentRole,
    is_primary_contact: resident.is_primary,
  })

  const updateResidentMut = useUpdateResident()
  const [idDocFile, setIdDocFile] = React.useState<File | null>(null)
  const [idDocPreview, setIdDocPreview] = React.useState<string | null>(resident.id_document_image ?? null)
  const [idDocError, setIdDocError] = React.useState<string | null>(null)
  const idDocInputRef = React.useRef<HTMLInputElement>(null)

  function handleIdDocEditChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null
    if (!file) {
      setIdDocFile(null)
      setIdDocError(null)
      return
    }

    const validationMessage = getResidentIdDocValidationMessage(file, t)
    if (validationMessage) {
      setIdDocFile(null)
      setIdDocError(validationMessage)
      if (idDocInputRef.current) idDocInputRef.current.value = ""
      return
    }

    setIdDocFile(file)
    setIdDocError(null)
    setIdDocPreview(URL.createObjectURL(file))
  }

  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (idDocError) {
      toast.error(idDocError)
      return
    }

    try {
      const updateData: UpdateResidentData = {
        full_name: `${formData.first_name} ${formData.last_name}`,
        email: formData.email,
        phone: formData.phone || undefined,
        cedula: formData.cedula || undefined,
        id_document_image: idDocFile || undefined,
        role: formData.resident_type,
        is_primary: formData.is_primary_contact,
      }

      await updateResidentMut.mutateAsync({ id: resident.id, data: updateData })

      toast.success(t("hoa.buildingDetail.toast.residentUpdated"), {
        description: t("hoa.buildingDetail.toast.residentUpdatedDescription", {
          name: `${formData.first_name} ${formData.last_name}`,
        }),
      })

      onClose()
    } catch (error: any) {
      console.error("Failed to update resident:", error)

      let errorMessage = t("error.generic")
      if (error.response?.data) {
        const data = error.response.data
        if (data.detail) {
          errorMessage = data.detail
        } else if (data.id_document_image) {
          errorMessage = Array.isArray(data.id_document_image)
            ? data.id_document_image[0]
            : data.id_document_image
        } else if (data.email) {
          errorMessage = Array.isArray(data.email) ? data.email[0] : data.email
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors
        } else if (typeof data === 'string') {
          errorMessage = data
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(t("hoa.buildingDetail.toast.residentUpdateFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden rounded-3xl bg-white/95 backdrop-blur-xl ring-1 ring-black/10">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("hoa.buildingDetail.residents.modal.edit")}</DialogTitle>
          <DialogDescription>
            {t("hoa.buildingDetail.residents.modal.editDescription")} <strong>{buildingName}</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="overflow-y-auto px-1 -mx-1 space-y-4 py-4" style={{ maxHeight: "calc(85vh - 180px)" }}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_first_name">
                  {t("hoa.buildingDetail.residents.form.firstName")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_first_name"
                  value={formData.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder={t("hoa.buildingDetail.residents.form.firstNamePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_last_name">
                  {t("hoa.buildingDetail.residents.form.lastName")} <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="edit_last_name"
                  value={formData.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder={t("hoa.buildingDetail.residents.form.lastNamePlaceholder")}
                  required
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_email">{t("hoa.buildingDetail.residents.form.email")}<span className="text-red-500">*</span>
              </Label>
              <Input
                id="edit_email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder={t("hoa.buildingDetail.residents.form.emailPlaceholder")}
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_phone">{t("hoa.buildingDetail.residents.form.phone")}</Label>
              <PhoneInputField
                id="edit_phone"
                value={formData.phone}
                onChange={(val) => handleChange("phone", val || "")}
                placeholder={t("hoa.buildingDetail.residents.form.phonePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_cedula">{t("hoa.buildingDetail.residents.form.identityDocument")}</Label>
              <Input
                id="edit_cedula"
                value={formData.cedula}
                onChange={(e) => handleChange("cedula", e.target.value)}
                placeholder={t("hoa.buildingDetail.residents.form.identityPlaceholder")}
                className="rounded-xl"
              />
              <p className="text-xs text-gray-400">{t("hoa.buildingDetail.residents.form.identityHelper")}</p>
            </div>

            {/* ID Document Image */}
            <div className="space-y-2">
              <Label htmlFor="edit_id_doc">{t("hoa.buildingDetail.residents.form.idDocumentPhoto")} <span className="text-gray-400 text-xs font-normal">({t("hoa.buildingDetail.optional")})</span></Label>
              <p className="text-xs text-gray-400">{t("hoa.buildingDetail.residents.form.idDocumentReplaceHelper")}</p>
              {idDocPreview && (
                <div className="relative inline-block">
                  <img src={idDocPreview} alt={t("hoa.buildingDetail.residents.form.idPreviewAlt")} className="h-24 w-auto rounded-xl object-contain border border-black/10 bg-gray-50" />
                  <button
                    type="button"
                    onClick={() => {
                      setIdDocFile(null)
                      setIdDocPreview(null)
                      setIdDocError(null)
                      if (idDocInputRef.current) idDocInputRef.current.value = ""
                    }}
                    className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center shadow ring-2 ring-white"
                  >✕</button>
                </div>
              )}
              <Input ref={idDocInputRef} id="edit_id_doc" type="file" accept="image/*" onChange={handleIdDocEditChange} className="rounded-xl text-sm" />
              {idDocError && <p className="text-xs text-rose-600">{idDocError}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_resident_type">{t("hoa.buildingDetail.residents.form.role")}</Label>
              <Select value={formData.resident_type} onValueChange={(value) => handleChange("resident_type", value)}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t("hoa.buildingDetail.residents.form.selectType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">{t("hoa.buildingDetail.residents.role.owner")}</SelectItem>
                  <SelectItem value="tenant">{t("hoa.buildingDetail.residents.role.tenant")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50">
              <div className="space-y-0.5">
                <Label htmlFor="edit_is_primary_contact" className="text-sm font-medium">
                  {t("hoa.buildingDetail.residents.form.primaryContact")}
                </Label>
                <p className="text-xs text-gray-500">
                  {t("hoa.buildingDetail.residents.form.primaryContactHelper")}
                </p>
              </div>
              <Switch
                id="edit_is_primary_contact"
                checked={formData.is_primary_contact}
                onCheckedChange={(checked) => handleChange("is_primary_contact", checked)}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={updateResidentMut.isPending}
              className={QUIET}
            >{t("common.cancel")}</Button>
            <Button
              type="submit"
              disabled={updateResidentMut.isPending}
              style={{ backgroundColor: PRIMARY }}
              className="text-white hover:opacity-90 rounded-xl shadow-lg hover:shadow-xl"
            >
              {updateResidentMut.isPending ? t("hoa.buildingDetail.residents.form.updating") : t("hoa.buildingDetail.residents.updateResident")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** =========================================================
 * Delete Resident Dialog Component
 * ========================================================= */
interface DeleteResidentDialogProps {
  open: boolean
  onClose: () => void
  resident: ResidentList
}

function DeleteResidentDialog({ open, onClose, resident }: DeleteResidentDialogProps) {
  const { t } = useLanguage();
  const deleteResidentMut = useDeleteResident()

  const handleDelete = async () => {
    try {
      await deleteResidentMut.mutateAsync(resident.id)

      toast.success(t("hoa.buildingDetail.toast.residentDeleted"), {
        description: t("hoa.buildingDetail.toast.residentDeletedDescription", { name: resident.full_name }),
      })

      onClose()
    } catch (error: any) {
      console.error("Failed to delete resident:", error)

      let errorMessage = t("hoa.buildingDetail.toast.residentDeleteErrorDescription")
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail
      } else if (error.message) {
        errorMessage = error.message
      }

      toast.error(t("hoa.buildingDetail.toast.residentDeleteFailed"), {
        description: errorMessage,
      })
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("hoa.buildingDetail.residents.deleteTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{t("hoa.buildingDetail.residents.deleteDescription", { name: resident.full_name, unit: resident.unit_code })}
            {resident.is_primary && (
              <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm font-medium">
                  {t("hoa.buildingDetail.residents.deletePrimaryWarning")}
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteResidentMut.isPending}>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteResidentMut.isPending}
          >
            {deleteResidentMut.isPending ? t("hoa.buildingDetail.deleting") : t("hoa.buildingDetail.residents.modal.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
